/**
 * Per-membership config (`/me/config`) — the server-backed replacement for the
 * browser-localStorage dismissal flags that used to gate "reminder" banners.
 *
 * The full list is fetched once and cached for the session; individual reminder
 * sites read their flag from cache via `useDismissible(key)`. Dismissing is
 * optimistic so the banner collapses instantly, then syncs to the server (and
 * therefore across the member's devices).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getConfig, upsertConfig } from '../../api/me';
import { learnerKeys } from '../../pages/learner/DashboardPage';
import type { MemberConfigEntry, MemberConfigUpsert } from '../../types/memberConfig';

const noAuthRetry = (count: number, err: unknown) => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status !== 401 && status !== 403 && count < 2;
};

/** Fetch all of the caller's config entries (cached; rarely changes). */
export function useMemberConfig() {
  return useQuery({
    queryKey: learnerKeys.config,
    queryFn: getConfig,
    staleTime: 5 * 60_000,
    retry: noAuthRetry,
  });
}

/** Merge an upsert into a cached entry list (immutably). */
function applyUpsert(
  entries: MemberConfigEntry[],
  body: MemberConfigUpsert
): MemberConfigEntry[] {
  const existing = entries.find((e) => e.key === body.key);
  const next: MemberConfigEntry = {
    key: body.key,
    enabled: body.enabled ?? existing?.enabled ?? true,
    value: body.value !== undefined ? body.value : existing?.value ?? null,
    payload: body.payload !== undefined ? body.payload : existing?.payload ?? null,
    updated_at: existing?.updated_at ?? null,
  };
  if (!existing) return [...entries, next];
  return entries.map((e) => (e.key === body.key ? next : e));
}

/** Upsert a config entry with an optimistic cache update + rollback. */
export function useUpsertConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MemberConfigUpsert) => upsertConfig(body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: learnerKeys.config });
      const previous = queryClient.getQueryData<MemberConfigEntry[]>(learnerKeys.config);
      queryClient.setQueryData<MemberConfigEntry[]>(learnerKeys.config, (old) =>
        applyUpsert(old ?? [], body)
      );
      return { previous };
    },
    onError: (_err, _body, context) => {
      if (context?.previous) {
        queryClient.setQueryData(learnerKeys.config, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: learnerKeys.config });
    },
  });
}

export interface UseDismissibleResult {
  /** True once the member has dismissed this reminder. */
  dismissed: boolean;
  /** Alias of `dismissed` for readability at call sites. */
  isDismissed: boolean;
  /** True while the config list is still loading (gate render to avoid flash). */
  isLoading: boolean;
  /** Persist the dismissal (optimistic). */
  dismiss: () => void;
}

/**
 * Reminder dismissal backed by `/me/config`. Render the reminder only when
 * `!isLoading && !dismissed` so a dismissed reminder never flashes.
 */
export function useDismissible(key: string): UseDismissibleResult {
  const { data, isLoading } = useMemberConfig();
  const upsert = useUpsertConfig();

  const dismissed = (data ?? []).some((e) => e.key === key && e.enabled);

  return {
    dismissed,
    isDismissed: dismissed,
    isLoading,
    dismiss: () => upsert.mutate({ key, enabled: true }),
  };
}
